import { useMemo } from "react";
import { ConferenceLink } from "./ConferenceLink";
import { TeamLink } from "./TeamLink";
import { TeamLogo } from "./TeamLogo";
import type {
  BasketballStandingsResponse,
  ConferencesResponse,
  RecordsStanding,
  TeamsResponse,
} from "./types";
import { useFetch } from "./useFetch";

const SPORT_LABEL: Record<string, string> = {
  "basketball-men": "Men's Basketball",
  "basketball-women": "Women's Basketball",
};

// endpointFor maps the SportID slug to the dedicated backend route.
// basketball-men and basketball-women have identical response shapes
// but separate routes so the standings service can scope its cache
// key and future per-sport logic (different seasons, etc.) cleanly.
function endpointFor(sport: string, division: string): string {
  return `/api/v1/standings/${encodeURIComponent(sport)}/${encodeURIComponent(division)}`;
}

function buildNameLookup(teams: TeamsResponse | null): Map<string, string> {
  const m = new Map<string, string>();
  if (!teams) return m;
  for (const t of teams.teams) {
    m.set(t.slug, t.shortName);
    m.set(t.slug.replace(/-/g, "_"), t.shortName);
    m.set(t.slug.replace(/_/g, "-"), t.shortName);
  }
  return m;
}

function titleCase(slug: string): string {
  return slug
    .split(/[-_]/)
    .map((p) => (p.length > 0 ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
}

export type BasketballStandingsPageProps = {
  sport: string;
  division: string;
  conference: string;
  onConferenceChange: (c: string) => void;
};

export function BasketballStandingsPage({
  sport,
  division,
  conference,
  onConferenceChange,
}: BasketballStandingsPageProps) {
  const standings = useFetch<BasketballStandingsResponse>(
    endpointFor(sport, division),
  );
  const teams = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const conferences = useFetch<ConferencesResponse>("/api/v1/conferences");

  const nameFor = useMemo(() => {
    const names = buildNameLookup(teams.kind === "ok" ? teams.data : null);
    return (slug: string) => names.get(slug) ?? titleCase(slug);
  }, [teams]);

  const conferenceLabelFor = useMemo(() => {
    const labels = new Map<string, string>();
    if (conferences.kind === "ok") {
      for (const c of conferences.data.conferences) {
        labels.set(c.slug, c.shortName || c.name);
      }
    }
    return (slug: string | undefined) =>
      slug ? (labels.get(slug) ?? slug) : "";
  }, [conferences]);

  const filtered: RecordsStanding[] = useMemo(() => {
    if (standings.kind !== "ok") return [];
    if (!conference) return standings.data.standings;
    return standings.data.standings.filter(
      (s) => s.conference === conference,
    );
  }, [standings, conference]);

  // Quadrants sidecar is keyed by slug. Omitted when NET data is
  // pre-season / empty; the UI collapses to records-only in that
  // case rather than rendering empty Q1-Q4 columns.
  const quadrants =
    standings.kind === "ok" ? (standings.data.quadrants ?? null) : null;
  const hasQuadrants = Boolean(quadrants);
  const activeConferenceLabel = conferenceLabelFor(conference);

  function wlt(w: number, l: number): string {
    return `${w}-${l}`;
  }

  return (
    <section className="rpi-page">
      <header className="rpi-header">
        <h2>
          {division.toUpperCase()} {SPORT_LABEL[sport] ?? sport} Standings
        </h2>
        <div className="rpi-controls">
          {conference && (
            <button type="button" onClick={() => onConferenceChange("")}>
              Clear conference filter
            </button>
          )}
          {!hasQuadrants && standings.kind === "ok" && (
            <span
              className="rpi-hint"
              title="NET + Quadrants sidecar is empty — the snapshot may be pre-season or not yet scraped."
            >
              NET pending
            </span>
          )}
        </div>
      </header>

      {standings.kind === "loading" && <p>Loading…</p>}
      {standings.kind === "error" && (
        <p role="alert">Failed to load standings: {standings.message}</p>
      )}
      {standings.kind === "ok" && filtered.length === 0 && (
        <p>
          {standings.data.seasonUnavailable
            ? `${SPORT_LABEL[sport] ?? sport} ${division.toUpperCase()} season hasn't started yet — standings will populate once games are played.`
            : `No ${SPORT_LABEL[sport] ?? sport} ${division.toUpperCase()}${conference ? ` ${activeConferenceLabel}` : ""} teams yet.`}
        </p>
      )}
      {standings.kind === "ok" && filtered.length > 0 && (
        <table className="standings">
          <thead>
            <tr>
              <th scope="col" className="num">
                Rank
              </th>
              <th scope="col">Team</th>
              <th scope="col">Conf</th>
              {hasQuadrants && (
                <th scope="col" className="num" title="NET rank">
                  NET
                </th>
              )}
              <th scope="col" className="num">
                W-L
              </th>
              <th scope="col" className="num" title="Win percentage">
                Win%
              </th>
              {hasQuadrants && (
                <>
                  <th
                    scope="col"
                    className="num"
                    title="Quadrant 1 record (opponent NET 1-30 home / 1-50 neutral / 1-75 road)"
                  >
                    Q1
                  </th>
                  <th scope="col" className="num" title="Quadrant 2 record">
                    Q2
                  </th>
                  <th scope="col" className="num" title="Quadrant 3 record">
                    Q3
                  </th>
                  <th scope="col" className="num" title="Quadrant 4 record">
                    Q4
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const q = quadrants?.[s.teamSlug];
              return (
                <tr key={s.teamSlug}>
                  <td className="num">{s.rank}</td>
                  <td>
                    <TeamLink
                      slug={s.teamSlug}
                      sport={sport}
                      division={division}
                    >
                      <TeamLogo slug={s.teamSlug} alt="" />
                      <span className="team-name">{nameFor(s.teamSlug)}</span>
                    </TeamLink>
                  </td>
                  <td>
                    <ConferenceLink
                      conference={s.conference ?? ""}
                      sport={sport}
                      division={division}
                      label={conferenceLabelFor(s.conference)}
                    />
                  </td>
                  {hasQuadrants && (
                    <td className="num">
                      {q?.netRank ? q.netRank : "—"}
                    </td>
                  )}
                  <td className="num">{wlt(s.wins, s.losses)}</td>
                  <td className="num">{s.winPercentage.toFixed(3)}</td>
                  {hasQuadrants && (
                    <>
                      <td className="num">
                        {q ? wlt(q.quad1.wins, q.quad1.losses) : "—"}
                      </td>
                      <td className="num">
                        {q ? wlt(q.quad2.wins, q.quad2.losses) : "—"}
                      </td>
                      <td className="num">
                        {q ? wlt(q.quad3.wins, q.quad3.losses) : "—"}
                      </td>
                      <td className="num">
                        {q ? wlt(q.quad4.wins, q.quad4.losses) : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
